import type { FC } from 'react';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { PaymentTermsContentKeys } from '@ctct/application-layer';
import { getEnv } from '@ctct/ctct-helpers';

import './payment-terms-content.scss';

interface PaymentTermsContentProps {
  contentKeys: PaymentTermsContentKeys;
  onTierPricingClick: () => void;
  showAutoRenewalTerms?: boolean;
  showChargesTerms?: boolean;
  showAcknowledgement?: boolean;
}

// Avoid ESLint accessibility warnings if we simply use `<a>` for the button.
const EmptyLink = 'a' as unknown as FC<JSX.IntrinsicElements['a']>;

function getLinkTargets() {
  return {
    terms: `https://www${getEnv()}constantcontact.com/legal/terms`,
    privacy: `https://www${getEnv()}constantcontact.com/legal/privacy-notice`,
    cancellation: `https://www${getEnv()}constantcontact.com/legal/terms#tc12`,
  };
}

export const PaymentTermsContent = ({
  contentKeys,
  onTierPricingClick,
  showAutoRenewalTerms = true,
  showChargesTerms = true,
  showAcknowledgement = true,
}: PaymentTermsContentProps) => {
  const { t } = useTranslation();

  const translationComponents = useMemo(() => {
    const linkTargets = getLinkTargets();

    return {
      strong: <strong />,
      TierPricing: (
        <EmptyLink
          role="button"
          className="link"
          onClick={(event) => {
            event.preventDefault();
            onTierPricingClick();
          }}
        />
      ),
      Terms: <EmptyLink target="_blank" rel="nofollow noreferrer" href={linkTargets.terms} />,
      Privacy: <EmptyLink target="_blank" rel="nofollow noreferrer" href={linkTargets.privacy} />,
      Cancellation: <EmptyLink target="_blank" rel="nofollow noreferrer" href={linkTargets.cancellation} />,
    };
  }, [onTierPricingClick]);

  const { autoRenewalTermsKey, chargesTermsKey, termsAcknowledgementKey, frequencyKey } = contentKeys;

  // Build translation values for multimonth terms
  const translationValues = frequencyKey ? { frequency: t(frequencyKey) } : undefined;

  return (
    <div className="payflow-presentation payflow-payment-terms">
      {showAutoRenewalTerms && autoRenewalTermsKey && (
        <p>
          <Trans
            i18nKey={autoRenewalTermsKey as string}
            values={translationValues}
            components={translationComponents}
          />
        </p>
      )}
      {showChargesTerms && chargesTermsKey && (
        <p>
          <Trans i18nKey={chargesTermsKey as string} components={translationComponents} />
        </p>
      )}
      {showAcknowledgement && (
        <p>
          <Trans i18nKey={termsAcknowledgementKey as string} components={translationComponents} />
        </p>
      )}
    </div>
  );
};
